import type { LocationQuery } from 'vue-router'
import { useSessionStorage } from '@vueuse/core'
import { computed } from 'vue'
import { decodeJwt } from '../utils/decodeJwt'
import { useFlashCookie } from './useFlashCookie'

interface RedirectablePage {
  path?: string
  query?: LocationQuery | undefined
  redirectable: boolean
}

interface RedirectablePages {
  prev?: RedirectablePage
  current: RedirectablePage
}

interface AuthData {
  redirect?: RedirectablePages
}

export function useReturnUrl() {
  return useFlashCookie<{ path: string, query: LocationQuery } | null | undefined>('return-url')
}
export function useAuthIntent() {
  return useFlashCookie<'login' | 'logout'>('auth-intent')
}

/**
 * @param {Route} _route - `required` when calling in middleware
 */
export function useAutobidAuth(_route?: ReturnType<typeof useRoute>) {
  const {
    signOut: sidebaseSignOut,
    signIn: sidebaseSignIn,
    status,
    data: sessionData,
  } = useAuth()
  const route = _route || useRoute()
  const redirectState = useSessionStorage(
    'auth-state',
    () => ({}) as Partial<AuthData>,
  )
  const intent = useAuthIntent()
  const returnUrl = useReturnUrl()
  const {
    SELF_URL,
    MY_AUTOBID_URL,
    autobidAuth: { authority: url, clientId },
  } = useRuntimeConfig().public
  const authDisabled = inject('authDisabled', false)
  const intentState = useState('intent-type', () => intent.consume()).value
  const returnUrlState = useState('return-url-type', () => returnUrl.consume()).value

  const authData = computed(() => (redirectState.value ?? {}) as AuthData)

  const isAuthed = computed(() => {
    if (authDisabled)
      return false

    return Boolean(
      status.value === 'authenticated'
      && sessionData.value?.user?.id
      && sessionData.value?.accessToken,
    )
  })

  const updateRedirectState = (data: Partial<AuthData>) => {
    redirectState.value = {
      ...(redirectState.value ?? {}),
      ...data,
    }
  }

  const getReturnTo = () => {
    const { path, query } = (() => {
      if (authData.value.redirect?.current.redirectable)
        return authData.value.redirect.current

      if (authData.value.redirect?.prev?.redirectable)
        return authData.value.redirect.prev

      return {
        path: `/`,
        query: {},
      }
    })()
    const url = new URL(SELF_URL)
    url.pathname = path as string

    if (query) {
      for (const key in query) {
        url.searchParams.append(key, query[key as keyof typeof query])
      }
    }
    return {
      url: url.toString(),
      path: path as string,
      query: query as LocationQuery,
    }
  }

  const setPageRedirectData = (routeData: RedirectablePage) => {
    const newRouteData = {
      ...routeData,
      ...(routeData.path
        ? {}
        : {
            path: route.path,
            query: route.query,
          }),
    }

    let redirectablePages = authData.value.redirect

    if (!redirectablePages) {
      redirectablePages = {
        current: newRouteData,
      }
    }
    else if (redirectablePages.current.path === newRouteData.path) {
      redirectablePages.current = {
        ...newRouteData,
        redirectable: redirectablePages.current.redirectable
          ? newRouteData.redirectable
          : false,
      }
    }
    else {
      if (redirectablePages.current.redirectable) {
        redirectablePages.prev = { ...redirectablePages.current }
      }

      redirectablePages.current = newRouteData
    }

    updateRedirectState({
      redirect: redirectablePages,
    })
  }

  const signIn = async () => {
    intent.set('login')
    await sidebaseSignIn('autobid', {
      redirect: true,
      callbackUrl: getReturnTo().url,
    })
  }

  const signOutFromMyAutobid = (logoutInSameWindow: boolean) => {
    if (!MY_AUTOBID_URL)
      return

    const MY_AUTOBID_LOGOUT_TIME = 1000
    const myAutobidIframe = document.createElement('iframe')
    myAutobidIframe.setAttribute(
      'src',
      `${MY_AUTOBID_URL}/?action=auth&show=logout`,
    )
    myAutobidIframe.setAttribute(
      'class',
      'absolute left-0 top-0 opacity-0 z-0 invisible',
    )
    document.body.appendChild(myAutobidIframe)

    if (logoutInSameWindow) {
      setTimeout(() => {
        myAutobidIframe.remove()
      }, 2000)
    }

    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(true)
      }, MY_AUTOBID_LOGOUT_TIME)
    })
  }

  const signOut = async (inSameWindow?: boolean) => {
    const returnTo = getReturnTo()
    returnUrl.set({
      path: returnTo.path,
      query: returnTo.query,
    })
    intent.set('logout')
    await Promise.all([
      signOutFromMyAutobid(!!inSameWindow),
      sidebaseSignOut({
        redirect: false,
      }),
    ])
    window.location.href = `${url}session/end?client_id=${clientId}&post_logout_redirect_uri=${SELF_URL}`
  }

  function getTokenData() {
    if (!sessionData.value?.accessToken)
      return null
    return decodeJwt(sessionData.value.accessToken)
  }

  return {
    authData,
    sessionData,
    isAuthed,
    getTokenData,
    setPageRedirectData,
    signIn,
    signOut,
    intent: intentState,
    returnUrl: returnUrlState,
  }
}
